import React, {
  useEffect,
  useState,
} from "react";

import {
  FaCheckCircle,
  FaEnvelope,
  FaIdCard,
  FaPen,
  FaPlus,
  FaPrint,
  FaTrash,
  FaUser,
} from "react-icons/fa";

import { toAbsoluteBadgeUrl } from "../../../../../services/validationBadgeServices";
import { toAbsoluteUserPhotoUrl } from "../../../../../services/userPhotoServices";

import { BADGE_THUMB } from "../data/usersAdmin.data";

import {
  extractActeurs,
  extractClubs,
  formatDateFR,
  getCreatedTime,
} from "../utils/usersAdmin.utils";

/* -------------------------------------------------------------------------- */
/*                               Avatar utilisateur                           */
/* -------------------------------------------------------------------------- */

function UserAvatar({
  src,
  alt,
}) {
  const [
    imageSrc,
    setImageSrc,
  ] = useState(
    toAbsoluteUserPhotoUrl(src) ||
      "/img/user_avatar.jpg",
  );

  useEffect(() => {
    setImageSrc(
      toAbsoluteUserPhotoUrl(src) ||
        "/img/user_avatar.jpg",
    );
  }, [src]);

  return (
    <img
      src={imageSrc}
      alt={alt}
      onError={() =>
        setImageSrc(
          "/img/user_avatar.jpg",
        )
      }
      className="h-10 w-10 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
    />
  );
}

/* -------------------------------------------------------------------------- */
/*                              Identité utilisateur                          */
/* -------------------------------------------------------------------------- */

export function UserIdentity({
  user,
}) {
  const createdTime =
    getCreatedTime(user);

  return (
    <div className="flex items-start gap-3">
      <UserAvatar
        src={user?.photoURL}
        alt={`${user?.nom || ""} ${user?.prenom || ""}`}
      />

      <div className="min-w-0">
        <div className="text-[15px] font-semibold leading-5 text-slate-900 dark:text-white">
          {user?.nom}{" "}
          {user?.prenom}
        </div>

        <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
          Créé le{" "}
          {createdTime
            ? formatDateFR(
                createdTime,
              )
            : "—"}
        </div>

        {user?.email ? (
          <a
            href={`mailto:${user.email}`}
            className="mt-1 block break-all text-[12px] text-indigo-600 hover:underline dark:text-indigo-300"
          >
            {user.email}
          </a>
        ) : (
          <div className="mt-1 text-[12px] text-slate-400">
            Email non renseigné
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                    Clubs                                   */
/* -------------------------------------------------------------------------- */

function ClubChip({
  club,
}) {
  const name =
    club?.nom_club ||
    club?.name ||
    "Club";

  const departement =
    club?.departement_club ||
    club?.departement ||
    "";

  return (
    <span className="inline-flex max-w-full items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[12px] text-slate-700 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-200">
      <span className="max-w-[190px] truncate font-medium">
        {name}
      </span>

      {departement ? (
        <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-700">
          {departement}
        </span>
      ) : null}
    </span>
  );
}

export function ClubsPreview({
  user,
  onLinkClub,
  max = 2,
}) {
  const clubs =
    extractClubs(user);

  const displayedClubs =
    clubs.slice(0, max);

  const hiddenCount =
    Math.max(
      0,
      clubs.length -
        displayedClubs.length,
    );

  return (
    <div className="space-y-2">
      {clubs.length ? (
        <div className="flex flex-wrap gap-1.5">
          {displayedClubs.map(
            (club, index) => (
              <ClubChip
                key={`${club?.id || club?.nom_club || "club"}-${index}`}
                club={club}
              />
            ),
          )}

          {hiddenCount > 0 ? (
            <span className="inline-flex items-center rounded-xl border border-slate-200 bg-slate-100 px-2.5 py-1.5 text-[12px] font-medium text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
              + {hiddenCount} autre
              {hiddenCount > 1
                ? "s"
                : ""}
            </span>
          ) : null}
        </div>
      ) : (
        <div className="text-xs text-slate-400">
          Aucun club lié
        </div>
      )}

      <button
        type="button"
        onClick={() =>
          onLinkClub(user)
        }
        className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-300"
      >
        <FaPlus />
        Lier un club
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Acteurs                                   */
/* -------------------------------------------------------------------------- */

function ActeurChip({
  acteur,
}) {
  const fullName =
    `${acteur?.nom || ""} ${acteur?.prenom || ""}`.trim() ||
    "Acteur";

  return (
    <span
      title={fullName}
      className="inline-flex max-w-full items-center gap-1.5 rounded-xl border border-cyan-200 bg-cyan-50 px-2.5 py-1.5 text-[12px] font-medium text-cyan-800 dark:border-cyan-500/20 dark:bg-cyan-500/10 dark:text-cyan-200"
    >
      <FaUser className="shrink-0 text-[10px] opacity-70" />

      <span className="max-w-[175px] truncate">
        {fullName}
      </span>
    </span>
  );
}

export function ActeursPreview({
  user,
  max = 2,
}) {
  const acteurs =
    extractActeurs(user);

  const displayedActeurs =
    acteurs.slice(0, max);

  const hiddenCount =
    Math.max(
      0,
      acteurs.length -
        displayedActeurs.length,
    );

  if (!acteurs.length) {
    return (
      <div className="text-xs text-slate-400">
        Aucun acteur lié
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {displayedActeurs.map(
        (acteur, index) => (
          <ActeurChip
            key={`${acteur?.id || acteur?.nom || "acteur"}-${index}`}
            acteur={acteur}
          />
        ),
      )}

      {hiddenCount > 0 ? (
        <span className="inline-flex items-center rounded-xl border border-slate-200 bg-slate-100 px-2.5 py-1.5 text-[12px] font-medium text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
          + {hiddenCount} autre
          {hiddenCount > 1
            ? "s"
            : ""}
        </span>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Badges                                    */
/* -------------------------------------------------------------------------- */

function BadgeThumb({
  badge,
  index,
  small,
}) {
  const url =
    badge?.badge_url
      ? toAbsoluteBadgeUrl(
          badge.badge_url,
        )
      : null;

  const dateText =
    badge?.validation_date
      ? formatDateFR(
          badge.validation_date,
        )
      : "—";

  const content = (
    <div
      title={`Badge validé le ${dateText}`}
      className="flex flex-col items-center"
    >
      <div
        className={`overflow-hidden rounded-lg border ${
          index === 0
            ? "border-indigo-300 ring-1 ring-indigo-500/60"
            : "border-slate-200 dark:border-slate-700"
        }`}
      >
        <img
          src={BADGE_THUMB}
          alt="Badge numérique"
          className={
            small
              ? "h-9 w-9 object-cover"
              : "h-10 w-10 object-cover"
          }
          loading="lazy"
        />
      </div>

      <span className="mt-1 whitespace-nowrap text-[9px] leading-none text-slate-400 dark:text-slate-500">
        {dateText}
      </span>
    </div>
  );

  if (!url) {
    return content;
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="shrink-0"
    >
      {content}
    </a>
  );
}

export function BadgesPreview({
  badges = [],
  small = false,
}) {
  if (!badges.length) {
    return (
      <span className="text-xs text-slate-400">
        Aucun badge
      </span>
    );
  }

  return (
    <div className="overflow-x-auto pb-1">
      <div className="flex w-max items-start gap-2">
        {badges.map(
          (badge, index) => (
            <BadgeThumb
              key={`${badge?.id || index}-${badge?.badge_url || "badge"}`}
              badge={badge}
              index={index}
              small={small}
            />
          ),
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                       Suivi administratif Carte Coach                       */
/* -------------------------------------------------------------------------- */

export function CoachBadgeStatusControls({
  user,
  canManageCoachBadge = false,
  onTogglePrinted,
  onToggleReceived,
  updatingField = null,
  compact = false,
}) {
  const printed =
    Boolean(
      user?.coach_badge_printed,
    );

  const received =
    Boolean(
      user?.coach_badge_received,
    );

  const shouldDisplayControls =
    canManageCoachBadge ||
    printed ||
    received;

  if (!shouldDisplayControls) {
    return (
      <div className="text-xs text-slate-400">
        Carte coach indisponible
      </div>
    );
  }

  const isUpdating =
    Boolean(updatingField);

  const printedLoading =
    updatingField === "printed";

  const receivedLoading =
    updatingField === "received";

  return (
    <div
      className={
        compact
          ? "grid grid-cols-2 gap-2"
          : "flex flex-col gap-2"
      }
    >
      {/* Imprimé */}
      <button
        type="button"
        aria-pressed={printed}
        onClick={() =>
          onTogglePrinted?.(user)
        }
        disabled={
          !onTogglePrinted ||
          isUpdating
        }
        title={
          printed
            ? "Carte coach imprimée - cliquer pour annuler"
            : "Marquer la carte coach comme imprimée"
        }
        className={`inline-flex min-h-9 items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold transition ${
          printed
            ? "border-emerald-500 bg-emerald-500 text-white shadow-sm hover:bg-emerald-600"
            : "border-slate-300 bg-white text-slate-600 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:border-blue-500 dark:hover:bg-blue-500/10 dark:hover:text-blue-300"
        } disabled:cursor-wait disabled:opacity-60`}
      >
        <FaPrint
          className={
            printedLoading
              ? "animate-pulse"
              : ""
          }
        />

        <span>
          {printed
            ? "Imprimé"
            : "Non imprimé"}
        </span>
      </button>

      {/* Réceptionné */}
      <button
        type="button"
        aria-pressed={received}
        onClick={() =>
          onToggleReceived?.(user)
        }
        disabled={
          !onToggleReceived ||
          !printed ||
          isUpdating
        }
        title={
          !printed
            ? "Le badge doit d'abord être imprimé"
            : received
              ? "Badge réceptionné - cliquer pour annuler"
              : "Marquer le badge comme réceptionné"
        }
        className={`inline-flex min-h-9 items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold transition ${
          received
            ? "border-emerald-500 bg-emerald-500 text-white shadow-sm hover:bg-emerald-600"
            : "border-slate-300 bg-white text-slate-600 hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:border-emerald-500 dark:hover:bg-emerald-500/10 dark:hover:text-emerald-300"
        } disabled:cursor-not-allowed disabled:opacity-40`}
      >
        <FaCheckCircle
          className={
            receivedLoading
              ? "animate-pulse"
              : ""
          }
        />

        <span>
          {received
            ? "Réceptionné"
            : "Non réceptionné"}
        </span>
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                  Actions                                    */
/* -------------------------------------------------------------------------- */

export function UserActions({
  user,
  onEdit,
  onDelete,
  onMessage,
  onCoachBadge,
  isCoachBadgeExporting = false,
  canGenerateCoachBadge = false,
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {onCoachBadge &&
      canGenerateCoachBadge ? (
        <button
          type="button"
          onClick={() =>
            onCoachBadge(user)
          }
          disabled={
            isCoachBadgeExporting
          }
          title="Générer la carte coach"
          className="inline-flex h-9 items-center justify-center rounded-xl bg-blue-950 px-3 text-sm text-white transition hover:bg-blue-900 disabled:cursor-wait disabled:opacity-60"
        >
          <FaIdCard
            className={
              isCoachBadgeExporting
                ? "animate-pulse"
                : ""
            }
          />
        </button>
      ) : null}

      <button
        type="button"
        onClick={() =>
          onEdit(user)
        }
        title="Éditer cet utilisateur"
        className="inline-flex h-9 items-center justify-center rounded-xl bg-indigo-600 px-3 text-sm text-white transition hover:bg-indigo-700"
      >
        <FaPen />
      </button>

      <button
        type="button"
        onClick={() =>
          onMessage(user)
        }
        title="Envoyer un message"
        className="inline-flex h-9 items-center justify-center rounded-xl bg-emerald-600 px-3 text-sm text-white transition hover:bg-emerald-700"
      >
        <FaEnvelope />
      </button>

      <button
        type="button"
        onClick={() =>
          onDelete(user.id)
        }
        title="Supprimer cet utilisateur"
        className="inline-flex h-9 items-center justify-center rounded-xl bg-red-600 px-3 text-sm text-white transition hover:bg-red-700"
      >
        <FaTrash />
      </button>
    </div>
  );
}